<template>
  <div class="sched">
    <!-- 部分时间表寄存器在该机组未配置：明确提示而不是下发时报“写入失败” -->
    <el-alert v-if="schedMappingIncomplete" type="info" :closable="false" class="sched-alert"
      :title="t('message.rtu.schedMappingIncomplete')" />
    <el-table :data="rows" size="default" :row-class-name="rowClass">
      <el-table-column :label="t('message.rtu.day')" width="130">
        <template #default="{ row }">
          <span class="day-name">{{ row.day ? t(`message.rtu.days.${row.day}`) : '' }}</span>
          <span v-if="isModified(row)" class="modflag">● {{ t('message.rtu.modified') }}</span>
        </template>
      </el-table-column>
      <el-table-column :label="t('message.rtu.occupiedFrom')" width="160">
        <template #default="{ row }">
          <el-select v-model="row.start" size="small" :placeholder="t('message.rtu.unoccupiedAllDay')"
            :disabled="!dayMapped(row.day, 'occs')" clearable filterable style="width: 130px">
            <el-option v-for="tm in TIME_OPTIONS" :key="tm" :label="tm" :value="tm" />
          </el-select>
        </template>
      </el-table-column>
      <el-table-column :label="t('message.rtu.occupiedUntil')" width="160">
        <template #default="{ row }">
          <el-select v-model="row.end" size="small" :placeholder="t('message.rtu.unoccupiedAllDay')"
            :disabled="!dayMapped(row.day, 'occe')" clearable filterable style="width: 130px">
            <el-option v-for="tm in TIME_OPTIONS" :key="tm" :label="tm" :value="tm" />
          </el-select>
        </template>
      </el-table-column>
      <el-table-column :label="t('message.rtu.smartRecovery')">
        <template #default="{ row }">
          <el-switch v-model="row.smart" size="small" :disabled="!dayMapped(row.day, 'smart')" />
        </template>
      </el-table-column>
    </el-table>

    <div class="holiday-row">
      <b>{{ t('message.rtu.holidays') }}</b>
      <el-tag v-for="h in holidays" :key="h.date" effect="plain" round>{{ h.label }} · {{ t('message.rtu.unoccupiedAllDay') }}</el-tag>
      <el-tag effect="plain" round type="info">+ {{ t('message.rtu.openSlots', { n: maxHolidays - holidays.length }) }}</el-tag>
    </div>

    <div class="sched-footer">
      <span v-if="pendingCount" class="pending">
        <b>{{ t('message.rtu.changesPending', { n: pendingCount }) }}</b> — {{ t('message.rtu.writesOnlyChanged') }}
      </span>
      <span v-else class="pending muted">{{ t('message.rtu.noChanges') }}</span>
      <span class="spacer" />
      <el-button :disabled="!pendingCount || writing" @click="discard">{{ t('message.rtu.discard') }}</el-button>
      <el-button type="primary" :disabled="!pendingCount" :loading="writing" @click="reviewApply">
        {{ t('message.rtu.reviewApply') }}
      </el-button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, defineProps } from 'vue';
import { useI18n } from 'vue-i18n';
import { ElMessageBox, ElMessage } from 'element-plus';

const props = defineProps({
  adapter: { type: Object, required: true },
  unitId: { type: String, required: true },
});
const { t } = useI18n();

// 00:00 … 23:45，15 分钟粒度（与 RTU 寄存器分辨率一致）
const TIME_OPTIONS = Array.from({ length: 96 }, (_, i) =>
  `${String(Math.floor(i / 4)).padStart(2, '0')}:${String((i % 4) * 15).padStart(2, '0')}`
);

const rows = ref([]);
const original = ref([]);
const holidays = ref([]);
const maxHolidays = ref(10);
const writing = ref(false);
// 该机组实际映射到的逻辑点名集合（空 = 无映射信息，全部可编辑，兼容旧/演示）
const mappedVars = ref(new Set());

const keyOf = (r) => `${r.start ?? ''}|${r.end ?? ''}|${r.smart}`;
const isModified = (row) => {
  const o = original.value.find((x) => x.day === row.day);
  return o && keyOf(o) !== keyOf(row);
};
const pending = computed(() => rows.value.filter(isModified));
const pendingCount = computed(() => pending.value.length);
const rowClass = ({ row }) => (isModified(row) ? 'mod-row' : '');

// kind: 'occs' | 'occe' | 'smart'。无映射信息时一律可编辑。
const dayMapped = (day, kind) =>
  !mappedVars.value.size || mappedVars.value.has(`sched_${kind}_${day}`);

// 有映射信息且存在未映射的时间表寄存器 → 提示配置不完整
const schedMappingIncomplete = computed(() =>
  mappedVars.value.size > 0 &&
  rows.value.some((r) => ['occs', 'occe', 'smart'].some((k) => !dayMapped(r.day, k)))
);

async function load() {
  const [s, mv] = await Promise.all([
    props.adapter.getSchedule(props.unitId),
    props.adapter.getMappedVars ? props.adapter.getMappedVars(props.unitId).catch(() => null) : null,
  ]);
  rows.value = s.days.map((d) => ({ ...d }));
  original.value = s.days.map((d) => ({ ...d }));
  holidays.value = s.holidays;
  maxHolidays.value = s.maxHolidays;
  if (mv) mappedVars.value = mv;
}

function discard() {
  rows.value = original.value.map((d) => ({ ...d }));
}

function validate() {
  for (const r of pending.value) {
    const hasStart = !!r.start, hasEnd = !!r.end;
    if (hasStart !== hasEnd) return t('message.rtu.schedBothTimes', { day: t(`message.rtu.days.${r.day}`) });
    if (hasStart && r.start >= r.end) return t('message.rtu.schedStartBeforeEnd', { day: t(`message.rtu.days.${r.day}`) });
  }
  return null;
}

async function reviewApply() {
  const err = validate();
  if (err) { ElMessage.warning(err); return; }
  const list = pending.value
    .map((r) => `${t(`message.rtu.days.${r.day}`)}: ${r.start ?? '—'}–${r.end ?? '—'}${r.smart ? ' ✓' : ''}`)
    .join('\n');
  try {
    await ElMessageBox.confirm(
      `${t('message.rtu.reviewBody')}\n\n${list}`,
      t('message.rtu.reviewTitle'),
      { confirmButtonText: t('message.rtu.applyConfirm'), cancelButtonText: t('message.rtu.cancel'), type: 'warning' }
    );
  } catch { return; }
  writing.value = true;
  try {
    const res = await props.adapter.writeSchedule(props.unitId, { days: pending.value.map((r) => ({ ...r })) });
    if (res.failed?.length) {
      // 真实下发失败（通讯错误）
      ElMessage.error(t('message.rtu.writeFailed') + ': ' + res.failed.join(', '));
      await load(); // 读回机组实际值
    } else if (res.unmapped?.length) {
      // 未映射：该机组模板未配置这些寄存器——引导查映射，不算下发失败
      ElMessage.warning(t('message.rtu.writeUnmapped', { vars: res.unmapped.join(', ') }));
    } else if (res.unconfirmed?.length) {
      // 已下发但设备尚未回报（真实网关回读周期长）：乐观保留本地新值，
      // 此时重读只会显示旧值造成困惑——不算失败（张之伦 2026-07-20）
      original.value = rows.value.map((r) => ({ ...r }));
      ElMessage.warning(t('message.rtu.writePending'));
    } else {
      ElMessage.success(t('message.rtu.schedApplied'));
      await load(); // 读回机组实际值
    }
  } catch (e) {
    console.error('[rtu] schedule write failed:', e);
    ElMessage.error(t('message.rtu.writeFailed'));
  } finally {
    writing.value = false;
  }
}

onMounted(load);
</script>

<style scoped>
.sched-alert { margin-bottom: 12px; }
.sched :deep(.mod-row) td { background: var(--el-color-primary-light-9); }
.day-name { font-weight: 600; }
.modflag { color: var(--el-color-primary); font-size: 11px; font-weight: 700; margin-left: 6px; }
.holiday-row { display: flex; align-items: center; gap: 10px; padding: 12px 0 0; font-size: 12.5px; flex-wrap: wrap; }
.sched-footer { display: flex; align-items: center; gap: 12px; margin-top: 12px; padding-top: 12px; border-top: 1px solid var(--el-border-color-lighter); }
.pending { font-size: 12.5px; color: var(--el-text-color-regular); }
.pending b { color: var(--el-color-primary); }
.pending.muted { color: var(--el-text-color-secondary); }
.spacer { flex: 1; }
</style>
